import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CameraCard from './CameraCard';
import { Box } from '@mui/material';
import { useCameraStatus } from './context/CameraStatusContext';

const CARD_ASPECT_RATIO = 9 / 16; // height / width for 16:9
const CARD_GAP = 16; // px gap between cards (same as `gap={4}` = 32px = 2rem)

const CameraGrid = ({ cameras, onCameraClick, theme, searchCamera = '', columns = 4, gridSize }) => {
	// console.log("Grid Camerac",cameras)
	const [placeholderCount, setPlaceholderCount] = useState(0);
	const [camerasToRender, setCamerasToRender] = useState([]);
	const { cameraStatusMap } = useCameraStatus();

	// Calculate initial placeholder count based on screen size
	useEffect(() => {
		const updatePlaceholderCount = () => {
			const screenHeight = window.innerHeight;
			const screenWidth = window.innerWidth;
			const containerWidth = screenWidth * 0.9;
			const cardWidth = containerWidth / columns;
			const cardHeight = cardWidth * CARD_ASPECT_RATIO;
			const totalVerticalSpace = screenHeight - 100;
			const rows = Math.floor(totalVerticalSpace / (cardHeight + CARD_GAP));
			setPlaceholderCount(rows * columns);
		};

		updatePlaceholderCount();
		window.addEventListener('resize', updatePlaceholderCount);
		return () => window.removeEventListener('resize', updatePlaceholderCount);
	}, [columns]);

	const [refreshProgress, setRefreshProgress] = useState(0);
	const refreshInterval = 30; // seconds for demo purposes (change to 1800 for 30 minutes)

	useEffect(() => {
		const sortAndSetCameras = () => {
			if (cameras.length > 0) {
				// Sort cameras: online cameras first, then offline cameras
				const sortedCameras = [...cameras].sort((a, b) => {
					const aStatus = cameraStatusMap[a.camera_id];
					const bStatus = cameraStatusMap[b.camera_id];

					// If both have the same status, maintain original order
					if (aStatus === bStatus) return 0;

					// Online cameras (true) come before offline cameras (false/undefined)
					if (aStatus === true && bStatus !== true) return -1;
					if (bStatus === true && aStatus !== true) return 1;

					return 0;
				});

				setCamerasToRender(sortedCameras);
			} else if (gridSize > 0) {
				setCamerasToRender(Array.from({ length: gridSize }));
			} else {
				setCamerasToRender(Array.from({ length: placeholderCount }));
			}

			// Reset progress when cameras are refreshed
			setRefreshProgress(0);
		};

		// Initial sort and set
		sortAndSetCameras();

		// Progress update timer (update every second)
		const progressTimer = setInterval(() => {
			setRefreshProgress((prev) => {
				const newProgress = prev + 100 / refreshInterval;
				return newProgress >= 100 ? 100 : newProgress;
			});
		}, 1000);

		// Set up refresh timer
		const refreshTimer = setInterval(() => {
			console.log('Refreshing camera grid');
			sortAndSetCameras();
		}, refreshInterval * 1000);

		// Clean up timers on component unmount
		return () => {
			clearInterval(refreshTimer);
			clearInterval(progressTimer);
		};
	}, [cameras, gridSize, placeholderCount, cameraStatusMap, refreshInterval]);

	const handleManualRefresh = () => {
		// Sort and refresh cameras
		if (cameras.length > 0) {
			const sortedCameras = [...cameras].sort((a, b) => {
				const aStatus = cameraStatusMap[a.camera_id];
				const bStatus = cameraStatusMap[b.camera_id];
				if (aStatus === bStatus) return 0;
				if (aStatus === true && bStatus !== true) return -1;
				if (bStatus === true && aStatus !== true) return 1;
				return 0;
			});
			setCamerasToRender(sortedCameras);
		}
		// Reset progress
		setRefreshProgress(0);
	};

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} sx={{ backgroundColor: '#f8fafb' }}>
			<Box
				display='grid'
				gap={3}
				gridTemplateColumns={`repeat(${columns}, 1fr)`}
				sx={{
					display: 'grid',
					gap: 3,
					gridTemplateColumns: `repeat(${columns}, 1fr)`,
					width: '100%',
					'@media (max-width: 600px)': {
						gridTemplateColumns: `repeat(1, 1fr)`,
					},
					margin: 0
				}}
			>
				{camerasToRender.map((camera, index) => (
					<motion.div key={camera?.camera_id || `placeholder-${index}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }} style={{ width: '100%', height: ' 100%' }}>
						<CameraCard camera={camera || null} onCameraClick={onCameraClick} theme={theme} />
					</motion.div>
				))}
			</Box>
		</motion.div>
	);
};

export default CameraGrid;
